import { IFaqFields, IProfessionalFields } from '../../../../../services/Event/EventService'
import { GeneralOverrideGroup, SeriesOverrideGroup, TicketType } from '../../../../../services/Event/types'
import { IEstablishmentTemplateForm } from '../../establishments/establishmentForm'
import { automaticSlug } from '../editionNumbering'
import { GeneralValue } from '../GeneralFieldsEditor'

/**
 * Shared by EventSeriesFormModal and its tab components. Lives outside the modal so
 * neither side of the Modal<->Tab import graph has to import the other just for these.
 */
export const ALL_GROUPS: SeriesOverrideGroup[] = [
  'slug',
  'format',
  'types',
  'thumb',
  'thumb_wide',
  'video_url',
  'copy',
  'target_audience',
  'topics',
  'term',
  'testimonial',
  'active_campaign',
  'help_form',
  'location'
]

export const isGeneralGroup = (group: SeriesOverrideGroup): group is GeneralOverrideGroup => group !== 'slug'

export const OVERRIDE_GROUP_LABELS: Record<SeriesOverrideGroup, string> = {
  slug: 'URL',
  format: 'Formato',
  types: 'Tipos',
  thumb: 'Thumb Padrão',
  thumb_wide: 'Thumb Wide',
  video_url: 'Vídeo da página',
  copy: 'Copy / Descrição',
  target_audience: 'A quem se destina',
  topics: 'Temas abordados',
  term: 'Termo',
  testimonial: 'Vídeo de depoimento',
  active_campaign: 'Listas Active Campaign',
  help_form: 'Tire suas dúvidas',
  location: 'Local'
}

export type EditionForm = GeneralValue & {
  _key: string
  id?: number
  name: string
  slug: string
  /**
   * The URL that is live on the site today, kept apart from `slug` (the field value, which
   * the form rewrites freely) so it survives any edit — it is what the change warning
   * compares against. Only set for editions loaded from the API.
   */
  publishedSlug?: string
  /**
   * The edition number the API assigned last save. When the edition hasn't moved (and the
   * base URL is unchanged), `publishedSlug` is the truth — it may carry a dedupe suffix
   * (`-2edicao-2`) the plain pattern can't reproduce, so we must not re-derive over it.
   */
  savedNumber?: number
  /**
   * The custom URL the user authored, kept apart from `slug` so toggling the `slug` tag off
   * and back on can restore it instead of overwriting it with the automatic one. Only set
   * for editions that load already overriding `slug`.
   */
  customSlugDraft?: string
  visible: boolean
  use_nfc: boolean
  date_start: string
  is_multiday: boolean
  date_end: string
  time_start: string
  time_end: string
  professionals: IProfessionalFields[]
  overrides: SeriesOverrideGroup[]
}

export type TemplateForm = {
  id?: number
  type: TicketType
  title: string
  quantity: number | ''
  price: number
  sales_start: string
  sales_end: string
  max_per_purchase: number | ''
  description: string
  visible: boolean
  fee_exempt: boolean
  is_child_ticket: boolean
  sell_at_door: boolean
  door_price: number | null
  edition_indexes: number[]
}

export type SeriesForm = GeneralValue & {
  name: string
  slug: string
  /** Cursos relacionados. Fora de GeneralValue de propósito: toda edição herda os
   *  cursos da série, então isto nunca vira um grupo de override. */
  course_ids: number[]
  /** Series-level FAQs (additive with each event's own FAQs; never overridden per edition). */
  faqs: IFaqFields[]
  editions: EditionForm[]
  ticket_templates: TemplateForm[]
  /** Modelos de estabelecimento (ABRA-317), aplicados às edições como os ingressos. */
  establishment_templates: IEstablishmentTemplateForm[]
}
/**
 * Value an edition's group should seed to when its override is turned on, mirrored from
 * the series' own current value. A pure function of its arguments (no closure over
 * component state) so callers can wrap themselves in `useCallback` with an empty
 * dependency array and stay safe to memoize — it only ever reads whatever `series`/
 * `edition` it's handed, never a stale one.
 */
export function seedGroupFrom(
  series: SeriesForm,
  group: SeriesOverrideGroup,
  edition: EditionForm,
  editionNumber: number
): Partial<EditionForm> {
  switch (group) {
    // Restore the URL the user had authored if there is one (so an off/on toggle doesn't
    // wipe it), otherwise seed the automatic one the field was already showing — never the
    // persisted slug, which would silently swap the value on screen. The published URL is
    // safe either way: it lives in `publishedSlug`, which the form never rewrites.
    case 'slug':
      return { slug: edition.customSlugDraft || automaticSlug(series.slug, editionNumber) || edition.slug }
    case 'format':
      return { format: series.format }
    case 'types':
      return { types: [...series.types] }
    case 'thumb':
      return { thumb: null, thumbPreview: series.thumbPreview }
    case 'thumb_wide':
      return { thumb_wide: null, thumbWidePreview: series.thumbWidePreview }
    case 'video_url':
      return { video: series.video, videoPreview: series.videoPreview, remove_video: series.remove_video }
    case 'copy':
      return { copy: series.copy }
    case 'target_audience':
      return { target_audience: series.target_audience }
    case 'topics':
      return { topics: series.topics }
    case 'term':
      return { use_term: series.use_term, term: series.term }
    case 'testimonial':
      return {
        use_testimonial: series.use_testimonial,
        testimonial_title: series.testimonial_title,
        testimonials: series.testimonials.map((s) => ({ ...s }))
      }
    case 'active_campaign':
      return {
        active_campaign_lead_lists: [...series.active_campaign_lead_lists],
        active_campaign_buyer_lists: [...series.active_campaign_buyer_lists]
      }
    case 'help_form':
      return { use_help_form: series.use_help_form }
    case 'location':
      return {
        zipcode: series.zipcode,
        street: series.street,
        number: series.number,
        complement: series.complement,
        neighborhood: series.neighborhood,
        city: series.city,
        state: series.state,
        latitude: series.latitude,
        longitude: series.longitude,
        place_id: series.place_id,
        place_name: series.place_name
      }
    default:
      return {}
  }
}
